import { useEffect, useMemo, useRef, useState } from 'react';
import {
  compositionsFromMission,
  compositionsFromWorkshop,
  createForcePreset,
  deleteForcePreset,
  fetchCompositionSources,
  fetchForcePresets,
  readComposition,
  updateForcePreset,
  uploadComposition,
  type CompositionCandidate,
  type ForcePresetBody,
  type ForcePresetView,
} from '../api/catalog';
import { ca } from '../i18n/ca';
import { CompositionFootprint } from './CompositionFootprint';
import { CompositionLibraryView } from './CompositionLibraryView';
import { WorkshopImport } from './WorkshopImport';
import { useConfirm } from './useConfirm';
import { eraLabel, useEras } from './useEras';

/**
 * Compositions: the other half of what an author places by hand.
 *
 * Eden places every sandbag, and the frameworks the group uses ship
 * Eden-exported compositions by the dozen
 * (docs/design/AUTHORING_GAPS_STUDY.md §6.2). None of it was reachable — the
 * platform could not read a composition at all — so the runtime built the ground
 * round an objective with a random bearing and no clearance test, and "a forward
 * base at the insertion point" was not something a mission could say.
 *
 * Three ways in, and they are the three that exist: the missions already on the
 * panel's volume, a pasted objects-mapper array, and a file somebody has on
 * their own machine. What comes back is a candidate, not a saved thing: it is
 * named and given a side before it is kept, because a composition with no name
 * is one nobody will ever find again.
 *
 * The picture is drawn, not photographed. A real preview needs the model's own
 * texture out of the game's PBOs; a footprint from the objects' offsets is what
 * the platform can honestly draw today, and it is enough to tell a checkpoint
 * from a camp at a glance.
 */

const c = ca.catalogPage2;
const SIDES = ['west', 'east', 'resistance', 'civilian'];

/** What a base with no eras written stands in: every era but the Second World War. */
const DEFAULT_ERAS = ['modern', 'coldwar', 'vietnam'];

function parseBody(json: string): ForcePresetBody {
  try {
    return JSON.parse(json) as ForcePresetBody;
  } catch {
    return {};
  }
}

export function Compositions({
  modSetHash,
  isAdmin = false,
}: {
  modSetHash: string | null;
  isAdmin?: boolean;
}) {
  /** The library first; the import forms behind their own tab (COMPOSITION_LIBRARY.md §6). */
  const [view, setView] = useState<'library' | 'import'>('library');
  const [confirm, confirmDialog] = useConfirm();
  const eraList = useEras();
  /** Every era a night can be set in, in the order the chips are drawn (era/theatres.json). */
  const ERAS = eraList.map((e) => e.id);
  const [saved, setSaved] = useState<ForcePresetView[]>([]);
  const [missions, setMissions] = useState<string[]>([]);
  const [workshop, setWorkshop] = useState<string[]>([]);
  const [mod, setMod] = useState('');
  const [folder, setFolder] = useState('');
  /** Read the base round this object, in its frame: the LHD helper for the group's deck. */
  const [anchor, setAnchor] = useState('');
  const [candidates, setCandidates] = useState<CompositionCandidate[]>([]);
  const [names, setNames] = useState<Record<string, string>>({});
  const [sides, setSides] = useState<Record<string, string>>({});
  const [pasted, setPasted] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const reload = useMemo(
    () => () => {
      if (!modSetHash) return;
      fetchForcePresets(modSetHash, 'composition')
        .then(setSaved)
        .catch(() => setSaved([]));
    },
    [modSetHash],
  );

  useEffect(() => {
    reload();
    fetchCompositionSources()
      .then((s) => {
        setMissions(s.missions);
        setWorkshop(s.workshop);
      })
      .catch(() => {
        setMissions([]);
        setWorkshop([]);
      });
  }, [reload]);

  const show = (found: CompositionCandidate[]) => {
    setCandidates(found);
    setNote(found.length === 0 ? c.compositionNothingFound : null);
  };

  async function scan() {
    if (!modSetHash || !folder) return;
    setBusy(true);
    setError(null);
    try {
      show(await compositionsFromMission(modSetHash, folder, anchor.trim() || undefined));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function scanMod() {
    if (!modSetHash || !mod) return;
    setBusy(true);
    setError(null);
    try {
      show(await compositionsFromWorkshop(modSetHash, mod));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function read() {
    if (!modSetHash || !pasted.trim()) return;
    setBusy(true);
    setError(null);
    try {
      show([await readComposition(modSetHash, c.compositionPaste, pasted)]);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function upload(file: File) {
    if (!modSetHash) return;
    setBusy(true);
    setError(null);
    try {
      show([await uploadComposition(modSetHash, file)]);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function keep(candidate: CompositionCandidate) {
    if (!modSetHash) return;
    setError(null);
    try {
      await createForcePreset(modSetHash, {
        kind: 'composition',
        name: names[candidate.name] || candidate.name,
        side: sides[candidate.name] || 'west',
        bodyJson: candidate.body,
      });
      setCandidates((rows) => rows.filter((r) => r !== candidate));
      reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  async function remove(id: number) {
    if (
      !(await confirm({
        title: c.compositionDeleteConfirm,
        confirmLabel: c.forceDelete,
        danger: true,
      }))
    ) {
      return;
    }
    await deleteForcePreset(id).catch(() => undefined);
    reload();
  }

  /** The eras a saved base stands in; with none written, every one but the Second World War. */
  const erasOf = (body: ForcePresetBody & { eras?: string[] }) =>
    body.eras && body.eras.length > 0 ? body.eras.map((e) => e.toLowerCase()) : DEFAULT_ERAS;

  /**
   * A base belongs to eras (`ForcePresetService.eras`): the group's Hesco FOBs
   * are base-game objects every server has, so nothing but this keeps them out
   * of 1942, and a camp built for the period has to be able to say so.
   */
  async function toggleEra(p: ForcePresetView, era: string) {
    let body: Record<string, unknown>;
    try {
      body = JSON.parse(p.bodyJson) as Record<string, unknown>;
    } catch {
      return;
    }
    const current = erasOf(body as ForcePresetBody);
    const next = current.includes(era) ? current.filter((e) => e !== era) : [...current, era];
    if (next.length === 0) return;
    body.eras = ERAS.filter((e) => next.includes(e));
    try {
      await updateForcePreset(p.id, {
        name: p.name,
        side: p.side,
        factionClass: p.factionClass ?? null,
        bodyJson: JSON.stringify(body),
      });
      reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  return (
    <section className="catalog-tab" aria-label={c.tabCompositions}>
      <div className="row wrap" role="group" aria-label={c.tabCompositions}>
        <button
          type="button"
          className={`chip toggle ${view === 'library' ? 'on' : ''}`}
          aria-pressed={view === 'library'}
          onClick={() => setView('library')}
        >
          <i className="bi bi-images" aria-hidden="true" /> {c.library.viewLibrary}
        </button>
        <button
          type="button"
          className={`chip toggle ${view === 'import' ? 'on' : ''}`}
          aria-pressed={view === 'import'}
          onClick={() => setView('import')}
        >
          <i className="bi bi-cloud-download" aria-hidden="true" /> {c.library.viewImport}
        </button>
      </div>
      {view === 'library' && <CompositionLibraryView modSetHash={modSetHash} isAdmin={isAdmin} />}
      {view === 'import' && (
        <>
          <p className="muted small">{c.compositionsIntro}</p>
          {!modSetHash && <p className="muted small">{c.forceNeedsScan}</p>}
          {error && (
            <p className="error" role="alert">
              {error}
            </p>
          )}

          {isAdmin && <WorkshopImport modSetHash={modSetHash} />}

          <div className="card form" role="group" aria-label={c.compositionFromMission}>
            <h3>{c.compositionFromMission}</h3>
            <div className="row">
              <select
                aria-label={c.compositionFromMission}
                value={folder}
                onChange={(e) => setFolder(e.target.value)}
              >
                <option value="">—</option>
                {missions.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
              <button type="button" disabled={!folder || busy} onClick={scan}>
                {c.compositionScan}
              </button>
            </div>
            <label>
              {c.compositionAnchor}
              <input
                type="text"
                value={anchor}
                placeholder="ATLAS_B_LHD_helper"
                onChange={(e) => setAnchor(e.target.value)}
              />
            </label>
            <p className="muted small">{c.compositionAnchorHint}</p>

            {workshop.length > 0 && (
              <>
                <h3>{c.compositionFromWorkshop}</h3>
                <p className="muted small">{c.compositionWorkshopHint}</p>
                <div className="row">
                  <select
                    aria-label={c.compositionFromWorkshop}
                    value={mod}
                    onChange={(e) => setMod(e.target.value)}
                  >
                    <option value="">—</option>
                    {workshop.map((id) => (
                      <option key={id} value={id}>
                        {id}
                      </option>
                    ))}
                  </select>
                  <button type="button" disabled={!mod || busy} onClick={scanMod}>
                    {c.compositionScan}
                  </button>
                </div>
              </>
            )}

            <h3>{c.compositionPaste}</h3>
            <p className="muted small">{c.compositionPasteHelp}</p>
            <textarea
              aria-label={c.compositionPaste}
              rows={4}
              value={pasted}
              onChange={(e) => setPasted(e.target.value)}
            />
            <div className="row">
              <button type="button" disabled={!pasted.trim() || busy} onClick={read}>
                {c.compositionRead}
              </button>
              <input
                ref={fileInput}
                type="file"
                accept=".sqf,.sqm,.hpp,.txt"
                hidden
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) void upload(file);
                  e.target.value = '';
                }}
              />
              <button type="button" disabled={busy} onClick={() => fileInput.current?.click()}>
                {c.compositionUpload}
              </button>
            </div>
            {note && <p className="muted small">{note}</p>}
          </div>

          {candidates.length > 0 && (
            <div className="card form" role="group" aria-label={c.compositionFound}>
              <h3>{c.compositionFound}</h3>
              <ul className="list compositions">
                {candidates.map((candidate) => (
                  <li key={candidate.name}>
                    <CompositionFootprint body={parseBody(candidate.body)} size={72} />
                    <span className="grow">
                      <strong>{candidate.name}</strong>
                      <span className="chip">
                        {c.compositionForms[candidate.form] ?? candidate.form}
                      </span>
                      <span className="chip">{c.compositionObjects(candidate.objects)}</span>
                      <span className="chip">{c.compositionRadius(candidate.radius)}</span>
                      {candidate.missing.length > 0 && (
                        <span className="chip warn">
                          {c.forceMissing(candidate.missing.length)}
                        </span>
                      )}
                    </span>
                    <input
                      aria-label={`${c.compositionName} ${candidate.name}`}
                      type="text"
                      placeholder={candidate.name}
                      value={names[candidate.name] ?? ''}
                      onChange={(e) =>
                        setNames((n) => ({ ...n, [candidate.name]: e.target.value }))
                      }
                    />
                    <select
                      aria-label={`${c.compositionSide} ${candidate.name}`}
                      value={sides[candidate.name] ?? 'west'}
                      onChange={(e) =>
                        setSides((s) => ({ ...s, [candidate.name]: e.target.value }))
                      }
                    >
                      {SIDES.map((s) => (
                        <option key={s} value={s}>
                          {ca.sideNames[s] ?? s}
                        </option>
                      ))}
                    </select>
                    <button type="button" className="primary" onClick={() => keep(candidate)}>
                      {c.compositionSave}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <h3>{c.compositionSaved}</h3>
          {saved.length === 0 && <p className="muted small">{c.compositionSavedEmpty}</p>}
          <ul className="list compositions">
            {saved.map((p) => {
              const body = parseBody(p.bodyJson);
              return (
                <li key={p.id}>
                  <CompositionFootprint body={body} size={72} />
                  <span className="grow">
                    <strong>{p.name}</strong>
                    <span className={`chip side-${p.side}`}>{ca.sideNames[p.side] ?? p.side}</span>
                    <span className="chip">
                      {c.compositionObjects((body.objects ?? []).length)}
                    </span>
                    {p.missing.length > 0 && (
                      <span className="chip warn">{c.forceMissing(p.missing.length)}</span>
                    )}
                  </span>
                  {(body as { baseFor?: string }).baseFor && (
                    <span className="row wrap" role="group" aria-label={c.compositionEras(p.name)}>
                      {ERAS.map((era) => (
                        <button
                          key={era}
                          type="button"
                          className={`chip toggle ${erasOf(body).includes(era) ? 'on' : ''}`}
                          aria-pressed={erasOf(body).includes(era)}
                          title={c.compositionErasHint}
                          onClick={() => void toggleEra(p, era)}
                        >
                          {eraLabel(eraList, era)}
                        </button>
                      ))}
                    </span>
                  )}
                  <button type="button" className="danger" onClick={() => remove(p.id)}>
                    {c.forceDelete}
                  </button>
                </li>
              );
            })}
          </ul>
        </>
      )}
      {confirmDialog}
    </section>
  );
}
